import React from 'react';
import { ChartLineIcon } from '@revfluence/fresh-icons/solid/esm';
import { FragmentType, graphql, useFragment } from '@frontend/gql/social';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import MetricChangeIndicator from '@frontend/applications/Social/components/metric-change-indicator';
import { DateType } from '@frontend/applications/Social/types';
import {
  HighlightCardStat,
  HighlightCardStatLabel,
} from '@frontend/applications/Social/components/highlight-card';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipArrow,
  TooltipContent,
} from '@frontend/applications/Social/components/tooltip';
import { ProjectWidgetContainer } from '@frontend/app/containers/Projects/OverviewPage/Widgets/common/ProjectWidgetContainer';
import { formatFullCurrency } from '@frontend/applications/Social/utils/formatNumber';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';

export const tmvCardFragment = graphql(`
  fragment TMVCard_TotalInsights on TotalInsights {
    tmv
  }
`);

interface TMVCardProps {
  data: FragmentType<typeof tmvCardFragment> | null | undefined;
  previousPeriodData?: FragmentType<typeof tmvCardFragment> | null | undefined;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  showComparisonTooltip?: boolean;
  isLoading?: boolean;
}

export const TMVCard: React.FC<TMVCardProps> = ({
  data,
  previousPeriodData,
  startDate,
  endDate,
  dateType,
  showComparisonTooltip = false,
  isLoading = false,
}) => {
  const totalInsights = useFragment(tmvCardFragment, data);
  const previousTotalInsights = useFragment(tmvCardFragment, previousPeriodData);

  const value = totalInsights?.tmv ?? 0;
  const previousValue = previousTotalInsights?.tmv ?? 0;

  const formattedValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

  const fullFormattedValue = formatFullCurrency(value);

  if (isLoading) {
    return (
      <ProjectWidgetContainer
        title="TMV"
        icon={<ChartLineIcon className="text-green-6" />}
        tooltip="Total Media Value (TMV) represents the estimated monetary value of content performance,
                        calculated based on engagement, reach, and impressions across all selected posts. This value is
                        filtered by the selected dates, projects, and groups."
        className="w-full"
      >
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      </ProjectWidgetContainer>
    );
  }

  return (
    <TooltipProvider>
      <ProjectWidgetContainer
        title="TMV"
        icon={<ChartLineIcon className="text-green-6" />}
        tooltip="Total Media Value (TMV) represents the estimated monetary value of content performance,
                        calculated based on engagement, reach, and impressions across all selected posts. This value is
                        filtered by the selected dates, projects, and groups."
        className="w-full"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <HighlightCardStat
              className="text-2xl leading-none"
              role="status"
              aria-label={`Total media value: ${formattedValue}`}
              data-dd-action-name="tmv-stat"
            >
              {formattedValue}
            </HighlightCardStat>
          </TooltipTrigger>
          <TooltipContent role="tooltip" aria-live="polite">
            <TooltipHeader>
              <TooltipTitle>{fullFormattedValue} Total Media Value</TooltipTitle>
            </TooltipHeader>
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>

        {previousPeriodData && (
          <MetricChangeIndicator
            current={value}
            previous={previousValue}
            startDate={startDate}
            endDate={endDate}
            dateType={dateType}
            showTimeRange={false}
            showComparisonTooltip={showComparisonTooltip}
          />
        )}
        <HighlightCardStatLabel className="mt-2">Total Media Value</HighlightCardStatLabel>
      </ProjectWidgetContainer>
    </TooltipProvider>
  );
};
